import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import { sumBy } from 'lodash';
import { getOS } from '../../utils';
import { openOrClosed } from '../../utils/intercom';
const styles = (theme) => ({
    wrapper: {
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        zIndex: 100,
    },
    scrollTarget: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    inCaptionMore: {
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    newTranscriptsButton: {
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 4,
        position: 'fixed',
        bottom: 20,
        right: 20,
        backgroundColor: theme.palette.ava.blue2,
        border: 'none',
        '-webkit-app-region': 'no-drag',
        '&:hover': {
            backgroundColor: theme.palette.ava.blue1,
            cursor: 'pointer',
        },
        zIndex: 1,
    },
});
class ScribeScrollWithTranscript extends React.Component {
    constructor(props) {
        super(props);
        this.lastScrollTop = 0;
        this.lastScrollsUp = [];
        this.onWheel = (wheelEvent) => {
            if (wheelEvent.deltaY >= 0) {
                // Scroll down or sideways. We might have reached bottom, but this will be checked
                // in componentDidUpdate
                return;
            }
            // Scroll up, we should stop following.
            if (this.state.followingTranscript) {
                this.setState({
                    followingTranscript: false,
                    scrollHeightWhenStoppedFollowing: this.scrollableContainer ? this.scrollableContainer.scrollHeight : 0,
                    newTranscriptsWhenNotFollowing: false,
                    scrollInitiated: Date.now(),
                });
            }
        };
        this.onScroll = () => {
            const { followingTranscript, touching } = this.state;
            // We only listen to scroll events during touch events - in other situations
            // we rely on the `wheel` event.
            if (!touching)
                return;
            if (!this.scrollableContainer)
                return;
            const newScrollTop = this.scrollableContainer.scrollTop;
            const oldScrollTop = this.lastScrollTop || 0;
            this.lastScrollTop = newScrollTop;
            if (newScrollTop > oldScrollTop) {
                // Scrolling down which does not change whether or not we are following transcript
                return;
            }
            const now = new Date().getTime();
            this.lastScrollsUp.push({
                when: now,
                scroll: oldScrollTop - newScrollTop,
            });
            // We keep all the scrolls of the last second
            this.lastScrollsUp = this.lastScrollsUp.filter((scroll) => now - scroll.when < 1000);
            const amountOfScroll = sumBy(this.lastScrollsUp, 'scroll');
            if (amountOfScroll > 50 && followingTranscript) {
                this.setState({
                    followingTranscript: false,
                    scrollHeightWhenStoppedFollowing: this.scrollableContainer ? this.scrollableContainer.scrollHeight : 0,
                    newTranscriptsWhenNotFollowing: false,
                    scrollInitiated: Date.now(),
                });
            }
        };
        this.onTouchStart = () => {
            if (!this.state.touching) {
                this.setState({
                    touching: true,
                });
            }
        };
        this.onTouchEnd = () => {
            if (this.state.touching) {
                this.setState({
                    touching: false,
                });
            }
        };
        this.state = {
            followingTranscript: true,
            isAtBottomOfTranscript: true,
            scrollHeightWhenStoppedFollowing: 0,
            newTranscriptsWhenNotFollowing: false,
            touching: false,
            // When was the user-initiated scrolling started. This is important, because
            // some time passes between receiving 'wheel' event and the browser actually
            // performing the scroll. It's important that in this time we do not check
            // whether we are not at the bottom or not - because the user-initiated
            // scroll has just not come through yet.
            scrollInitiated: Date.now(),
        };
        this.scrollableContainer = null;
        // This will get re-registered every time this component gets recreated,
        // but it's okay since it's very lightweight. There is currently no way
        // in intercom to clean up callbacks.
        openOrClosed(props.setIntercomOpen);
    }
    componentDidUpdate(prevProps) {
        const { electronCaptionMode } = this.props;
        if (electronCaptionMode && !prevProps.electronCaptionMode) {
            /* eslint-disable-next-line react/no-did-update-set-state */
            this.setState({ followingTranscript: true });
        }
        this.updateIsAtBottomOfTranscript();
        this.updateNewTranscriptsWhenNotFollowing();
        this.followIfShouldFollow();
    }
    componentWillUnmount() {
        if (this.scrollableContainer && this.scrollableContainer.removeEventListener) {
            this.scrollableContainer.removeEventListener('wheel', this.onWheel);
            this.scrollableContainer.removeEventListener('scroll', this.onScroll);
            this.scrollableContainer.removeEventListener('touchstart', this.onTouchStart);
            this.scrollableContainer.removeEventListener('touchend', this.onTouchEnd);
        }
    }
    followIfShouldFollow() {
        const { followingTranscript } = this.state;
        if (!followingTranscript) {
            return;
        }
        // setTimeout(..., 0) makes the browser execute the code directly after
        // this execution finishes. Without it the browser is not done rendering
        // the container and is unable to scroll it.
        setTimeout(() => {
            if (!this.scrollableContainer)
                return;
            this.scrollableContainer.scrollTop = this.scrollableContainer.scrollHeight;
        }, 0);
    }
    updateIsAtBottomOfTranscript() {
        if (Date.now() - this.state.scrollInitiated < 100)
            return;
        if (!this.scrollableContainer)
            return;
        const newScrollTop = this.scrollableContainer.scrollTop;
        const scrollDistanceFromBottom = this.scrollableContainer.scrollHeight - (newScrollTop + this.scrollableContainer.clientHeight);
        const isAtBottomOfTranscript = scrollDistanceFromBottom < 30;
        const oldIsAtBottomOfTranscript = this.state.isAtBottomOfTranscript;
        if (isAtBottomOfTranscript !== oldIsAtBottomOfTranscript)
            this.setState({ isAtBottomOfTranscript });
        if (isAtBottomOfTranscript && !this.state.followingTranscript) {
            this.setState({ followingTranscript: true });
        }
        return isAtBottomOfTranscript;
    }
    updateNewTranscriptsWhenNotFollowing() {
        const { newTranscriptsWhenNotFollowing, followingTranscript, scrollHeightWhenStoppedFollowing } = this.state;
        if (followingTranscript)
            return;
        const newTranscripts = scrollHeightWhenStoppedFollowing !== (this.scrollableContainer && this.scrollableContainer.scrollHeight);
        if (newTranscriptsWhenNotFollowing !== newTranscripts) {
            this.setState({ newTranscriptsWhenNotFollowing: newTranscripts });
        }
    }
    render() {
        const { bottomMenu, children, electronCaptionMode, intercomOpen, fullScreen, lightView, keyboardInputShown, sidebar, classes, t, } = this.props;
        const { isAtBottomOfTranscript, followingTranscript, newTranscriptsWhenNotFollowing } = this.state;
        const headerHeight = fullScreen || lightView ? 0 : 99;
        return (React.createElement("div", { className: `${classes.wrapper} ${electronCaptionMode ? classes.inCaptionMore : ''}`, 
            // the IntersectionObserver in ScribeTranscriptList looks for this ID, do not remove
            id: "TRANSCRIPT_CONTAINER", style: {
                position: electronCaptionMode ? 'relative' : 'fixed',
                height: electronCaptionMode
                    ? '100%'
                    : `calc(100% - ${headerHeight}px - ${bottomMenu ? 4.5 : 0}rem - ${keyboardInputShown ? 70 : 0}px)`,
                width: electronCaptionMode ? '100%' : `calc(100% - ${sidebar ? 270 : 0}px)`,
                WebkitAppRegion: getOS() === 'Mac' && electronCaptionMode ? 'drag' : '',
                left: electronCaptionMode ? 0 : undefined,
                top: electronCaptionMode ? 0 : undefined,
                paddingRight: electronCaptionMode ? 'initial' : intercomOpen ? '450px' : '100px',
            }, ref: (el) => {
                if (el && this.scrollableContainer !== el) {
                    this.scrollableContainer = el;
                    this.scrollableContainer.addEventListener('wheel', this.onWheel);
                    this.scrollableContainer.addEventListener('scroll', this.onScroll);
                    this.scrollableContainer.addEventListener('touchstart', this.onTouchStart);
                    this.scrollableContainer.addEventListener('touchend', this.onTouchEnd);
                }
            } },
            !isAtBottomOfTranscript && !followingTranscript && newTranscriptsWhenNotFollowing && (React.createElement("button", { className: classes.newTranscriptsButton, onClick: () => {
                    this.lastScrollsUp = [];
                    this.setState({ followingTranscript: true }, () => this.followIfShouldFollow());
                } },
                t('ccMode.conversation.newTranscripts'),
                " \u2193")),
            React.createElement("div", { className: `${classes.scrollTarget} ${electronCaptionMode ? classes.inCaptionMore : ''}` }, children)));
    }
}
export default withTranslation()(withStyles(styles)(ScribeScrollWithTranscript));
